import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../utils/createAllPhaseMenuGroups';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Tabs,
  Tab,
  Checkbox,
  CircularProgress,
  Menu
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Book as BookIcon,
  Apps as AppsIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Home as PrepHomeIcon,
  Assignment as AssignmentIcon,
  Settings as PrepSettingsIcon,
  ContentCopy as CopyIcon,
  School as ClassIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import config from '../../../config.json';
import url from '../../../url.json';
import TopBannerGuidance from '../../../components/TopBannerGuidance';
import OwlMascot from '../../../assets/貓頭鷹博士_雙手.gif';

const ClassManagement = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openStudentManagementDialog, setOpenStudentManagementDialog] = useState(false);
  const [openGroupSettingDialog, setOpenGroupSettingDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [showOwlGuidance, setShowOwlGuidance] = useState(true);

  // 表單狀態
  const [formData, setFormData] = useState({
    name: '',
    year: '114',
    semester: ''
  });

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('preparation', 'communities', navigate);

  // 獲取社群列表
  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(config.getClasses);
      // 後端直接返回陣列，不需要檢查 success 屬性
      setClasses(response.data || []);
    } catch (error) {
      console.error('獲取社群列表錯誤:', error);
      setError('獲取社群列表失敗');
    } finally {
      setLoading(false);
    }
  };

  // 創建社群
  const handleCreateClass = async () => {
    try {
      const response = await axiosInstance.post(config.createClass, formData);
      if (response.data.message) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setOpenCreateDialog(false);
        setFormData({ name: '', year: '114', semester: '' });
        fetchClasses();
      } else {
        setSnackbar({ open: true, message: '創建失敗', severity: 'error' });
      }
    } catch (error) {
      console.error('創建社群錯誤:', error);
      setSnackbar({ open: true, message: '創建社群失敗', severity: 'error' });
    }
  };

  // 更新社群
  const handleUpdateClass = async () => {
    try {
      const response = await axiosInstance.put(`${config.updateClass}${selectedClass.id}`, formData);
      if (response.data.message) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setOpenEditDialog(false);
        setSelectedClass(null);
        setFormData({ name: '', year: '114', semester: '' });
        fetchClasses();
      } else {
        setSnackbar({ open: true, message: '更新失敗', severity: 'error' });
      }
    } catch (error) {
      console.error('更新社群錯誤:', error);
      setSnackbar({ open: true, message: '更新社群失敗', severity: 'error' });
    }
  };

  // 刪除社群
  const handleDeleteClass = async (classItem = null) => {
    try {
      const classToDelete = classItem || selectedClass;
      if (!classToDelete) {
        console.error('沒有選擇要刪除的社群');
        setSnackbar({ open: true, message: '沒有選擇要刪除的社群', severity: 'error' });
        return;
      }

      const response = await axiosInstance.delete(`${config.deleteClass}${classToDelete.id}`);
      if (response.data.message) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        setOpenDeleteDialog(false);
        setSelectedClass(null);
        fetchClasses();
      } else {
        setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
      }
    } catch (error) {
      console.error('刪除社群錯誤:', error);
      setSnackbar({ open: true, message: '刪除社群失敗', severity: 'error' });
    }
  };

  // 複製邀請碼
  const handleCopyInviteCode = async (inviteCode) => {
    try {
      await navigator.clipboard.writeText(inviteCode);
      setSnackbar({ open: true, message: '邀請碼已複製', severity: 'success' });
    } catch (error) {
      console.error('複製失敗:', error);
      // 備用方案：使用傳統方法
      const textArea = document.createElement('textarea');
      textArea.value = inviteCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setSnackbar({ open: true, message: '邀請碼已複製', severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: '複製失敗，請手動複製', severity: 'error' });
      }
      document.body.removeChild(textArea);
    }
  };

  // 重新生成邀請碼
  const handleRegenerateInviteCode = async (classId) => {
    try {
      const response = await axiosInstance.post(`${config.regenerateInviteCode}${classId}/regenerate-invite`);
      if (response.data.message) {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        fetchClasses();
      } else {
        setSnackbar({ open: true, message: '重新生成失敗', severity: 'error' });
      }
    } catch (error) {
      console.error('重新生成邀請碼錯誤:', error);
      setSnackbar({ open: true, message: '重新生成邀請碼失敗', severity: 'error' });
    }
  };

  // 打開編輯對話框
  const handleOpenEditDialog = (classItem) => {
    setSelectedClass(classItem);
    setFormData({
      name: classItem.name,
      year: classItem.year,
      semester: classItem.semester
    });
    setOpenEditDialog(true);
  };

  // 打開刪除對話框
  const handleOpenDeleteDialog = (classItem) => {
    setSelectedClass(classItem);
    setOpenDeleteDialog(true);
  };

  // 處理選單開啟
  const handleMenuOpen = (event, classId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedClassId(classId);
  };

  // 處理選單關閉
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClassId(null);
  };

  // 處理刪除確認
  const handleDeleteConfirm = async () => {
    if (selectedClassId) {
      const classItem = classes.find(c => c.id === selectedClassId);
      if (classItem && window.confirm(`確定要刪除社群「${classItem.name}」嗎？`)) {
        await handleDeleteClass(classItem);
        // 確保清理所有相關狀態
        setSelectedClassId(null);
        setSelectedClass(null);
      }
    }
    handleMenuClose();
  };

  // 篩選社群
  const filteredClasses = classes.filter(classItem => {
    const matchesSearch = classItem.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesYear = !filterYear || classItem.year === filterYear;
    const matchesSemester = !filterSemester || classItem.semester === filterSemester;
    return matchesSearch && matchesYear && matchesSemester;
  });

  // 獲取學年度選項
  const getYearOptions = () => {
    const years = [...new Set(classes.map(c => c.year))];
    return years.sort();
  };

  // 獲取學期選項
  const getSemesterOptions = () => {
    const semesters = [...new Set(classes.map(c => c.semester))];
    return semesters.sort();
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  if (loading) {
  return (
    <div className="teacher-layout class-management-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="preparation"
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
        />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teacher-layout class-management-container">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="preparation"
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
          />
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={fetchClasses}>
              重新載入
            </Button>
          }>
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-layout class-management-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="preparation"
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
        />
        <div className="class-header-row">
          <h1 className="main-title">社群管理</h1>
          {/* 篩選區域在標題下方置中 */}
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋社群"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
          />
          <FormControl size="small" className="filter-select">
            <InputLabel>學年度</InputLabel>
            <Select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              label="學年度"
            >
              <MenuItem value="">全部</MenuItem>
              {getYearOptions().map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" className="filter-select">
            <InputLabel>學期</InputLabel>
            <Select
              value={filterSemester}
              onChange={(e) => setFilterSemester(e.target.value)}
              label="學期"
            >
              <MenuItem value="">全部</MenuItem>
              {getSemesterOptions().map(semester => (
                <MenuItem key={semester} value={semester}>{semester}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <button
            className="btn-secondary filter-clear-btn"
            onClick={() => {
              setSearchTerm('');
              setFilterYear('');
              setFilterSemester('');
            }}
          >
            清除篩選
          </button>
          </div>
        </div>

        {/* 頂部橫幅提醒 */}
        <TopBannerGuidance
          show={showOwlGuidance}
          onClose={() => setShowOwlGuidance(false)}
          severity={classes.length > 0 ? 'success' : 'info'}
          mascotImage={OwlMascot}
          message={
            classes.length === 0
              ? '先建立班級（社群），然後將學生分配到班級中。建立班級後，就可以在「課程派發」中選擇班級來派發課程了。'
              : `目前有 ${classes.length} 個班級。記得為每個班級分配學生，並設定分組。完成後就可以前往「課程派發」選擇班級派發課程了。`
          }
        />

        {/* 社群列表 */}
        <div className="activity-card-list grid-responsive">
          {/* 新增社群卡片 */}
          <div
            className="activity-card add-activity"
            onClick={() => setOpenCreateDialog(true)}
            style={{ 
              background: '#2b687f',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              minHeight: '200px',
              maxHeight: '200px',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.02)';
              e.target.style.transition = 'all 0.3s ease';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.transition = 'all 0.3s ease';
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 48, mb: 1 }} />
              <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                建立社群
              </div>
            </div>
          </div>

          {filteredClasses.map((classItem) => (
            <div
              className="activity-card"
              key={classItem.id}
              style={{ 
                position: 'relative', 
                background:'#fff', 
                boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
                borderRadius: '12px', 
                border:'1.5px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                minHeight: '200px',
                maxHeight: '200px',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(44,44,80,0.12)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(44,44,80,0.08)';
              }}
            >
               <div className="activity-name" style={{ 
                 fontSize: '1.2rem', 
                 marginBottom: '2px',
                 padding: '2px 16px 0 16px',
                 overflow: 'hidden',
                 textOverflow: 'ellipsis',
                 display: '-webkit-box',
                 WebkitLineClamp: 2,
                 WebkitBoxOrient: 'vertical',
                 lineHeight: '1.4',
                 minHeight: '2.8rem',
                 fontWeight: 'bold'
               }}>
                 {classItem.name}
              </div>
              
               {/* 學年度標籤 */}
              <div style={{ 
                display: 'flex', 
                gap: '6px', 
                 marginBottom: '2px',
                 padding: '0 16px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}>
                  {classItem.year} {classItem.semester === '上' || classItem.semester === '下' ? `${classItem.semester}` : classItem.semester}
                </span>
              </div>
              
               {/* 邀請碼資訊 */}
               <div style={{ 
                 padding: '0 16px',
                 marginBottom: '8px'
               }}>
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#666', 
                  marginBottom: '4px',
                display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>邀請碼: {classItem.inviteCode}</span>
                  <CopyIcon 
                    sx={{ 
                      fontSize: '16px', 
                      cursor: 'pointer',
                      color: '#666',
                      '&:hover': { color: '#1976d2' }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyInviteCode(classItem.inviteCode);
                    }}
                  />
                </div>
              </div>

              {/* 操作按鈕 */}
              <div style={{ 
                  position: 'absolute',
                bottom: '12px',
                left: '12px',
                  right: '12px',
                display: 'flex', 
                gap: '8px',
                flexDirection: 'column'
              }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                      setSelectedClass(classItem);
                      setOpenStudentManagementDialog(true);
                  }}
                    style={{ flex: 1, fontSize: '0.7rem' }}
                >
                  學生管理
                </button>
                <button
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                      setSelectedClass(classItem);
                      setOpenGroupSettingDialog(true);
                  }}
                    style={{ flex: 1, fontSize: '0.7rem' }}
                >
                    分組設定
                </button>
              </div>
                  <button 
                  className="btn-card"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/teacher/preparation/course-dispatch?classId=${classItem.id}`);
                  }}
                  style={{ fontSize: '0.7rem' }}
                >
                  課程派發
                  </button>
                </div>

                {/* 選項選單 */}
                <div
                  className="activity-menu"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#666',
                    zIndex: 1
                  }}
                  onClick={(e) => handleMenuOpen(e, classItem.id)}
                >
                  ⋮
                </div>
            </div>
          ))}
        </div>

        {/* 創建社群對話框 */}
        <Dialog 
          open={openCreateDialog} 
          onClose={() => setOpenCreateDialog(false)} 
          maxWidth="sm" 
          fullWidth
          sx={{
            zIndex: 2000,
            '& .MuiDialog-paper': {
              zIndex: 2000
            }
          }}
          PaperProps={{
            sx: {
              zIndex: 2000
            }
          }}
        >
          <DialogTitle>建立社群</DialogTitle>
        <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="社群名稱"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel>學年度</InputLabel>
                <Select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  label="學年度"
                >
                  <MenuItem value="116">116學年度 (2027-2028)</MenuItem>
                  <MenuItem value="115">115學年度 (2026-2027)</MenuItem>
                  <MenuItem value="114">114學年度 (2025-2026)</MenuItem>
                  <MenuItem value="113">113學年度 (2024-2025)</MenuItem>
                  <MenuItem value="112">112學年度 (2023-2024)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>學期</InputLabel>
                <Select
                  value={formData.semester}
                  onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                  label="學期"
                >
                  <MenuItem value="上學期">上學期</MenuItem>
                  <MenuItem value="下學期">下學期</MenuItem>
                </Select>
              </FormControl>
                  </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setOpenCreateDialog(false)}>取消</Button>
            <Button onClick={handleCreateClass} variant="contained">建立</Button>
        </DialogActions>
      </Dialog>

        {/* 編輯社群對話框 */}
        <Dialog 
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)} 
          maxWidth="sm" 
          fullWidth
          sx={{
            zIndex: 2000,
            '& .MuiDialog-paper': {
              zIndex: 2000
            }
          }}
          PaperProps={{
            sx: {
              zIndex: 2000
            }
          }}
        >
          <DialogTitle>編輯社群</DialogTitle>
      <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="社群名稱"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel>學年度</InputLabel>
                <Select
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  label="學年度"
                >
                  <MenuItem value="116">116學年度 (2027-2028)</MenuItem>
                  <MenuItem value="115">115學年度 (2026-2027)</MenuItem>
                  <MenuItem value="114">114學年度 (2025-2026)</MenuItem>
                  <MenuItem value="113">113學年度 (2024-2025)</MenuItem>
                  <MenuItem value="112">112學年度 (2023-2024)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>學期</InputLabel>
                <Select
                  value={formData.semester}
                  onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
                  label="學期"
                >
                  <MenuItem value="上學期">上學期</MenuItem>
                  <MenuItem value="下學期">下學期</MenuItem>
                </Select>
              </FormControl>
                  </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setOpenEditDialog(false)}>取消</Button>
            <Button onClick={handleUpdateClass} variant="contained">更新</Button>
      </DialogActions>
        </Dialog>

        {/* 選項選單 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => {
            if (selectedClassId) {
              const classItem = classes.find(c => c.id === selectedClassId);
              if (classItem) {
                handleOpenEditDialog(classItem);
              }
            }
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} />
            編輯社群
          </MenuItem>
          <MenuItem onClick={handleDeleteConfirm}>
            <DeleteIcon sx={{ mr: 1 }} />
            刪除社群
          </MenuItem>
        </Menu>

        {/* 刪除確認對話框 */}
        <Dialog 
          open={openDeleteDialog} 
          onClose={() => setOpenDeleteDialog(false)}
          sx={{
            zIndex: 2000,
            '& .MuiDialog-paper': {
              zIndex: 2000
            }
          }}
          PaperProps={{
            sx: {
              zIndex: 2000
            }
          }}
        >
          <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
            <Typography>
              確定要刪除社群「{selectedClass?.name}」嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setOpenDeleteDialog(false)}>取消</Button>
            <Button onClick={handleDeleteClass} variant="contained">刪除</Button>
        </DialogActions>
      </Dialog>

        {/* 學生管理對話框 */}
        <StudentManagementDialog
          open={openStudentManagementDialog}
          classData={selectedClass}
          onClose={() => {
            setOpenStudentManagementDialog(false);
            setSelectedClass(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            // 不自動關閉對話框，讓用戶可以繼續操作
          }}
        />

        {/* 分組設定對話框 */}
        <GroupSettingDialog
          open={openGroupSettingDialog}
          classData={selectedClass}
          onClose={() => {
            setOpenGroupSettingDialog(false);
            setSelectedClass(null);
          }}
          onSuccess={(message) => {
            setSnackbar({ open: true, message, severity: 'success' });
            setOpenGroupSettingDialog(false);
            setSelectedClass(null);
          }}
        />

        {/* 成功/錯誤提示 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

// 學生管理對話框組件
function StudentManagementDialog({ open, classData, onClose, onSuccess }) {
  const [allStudents, setAllStudents] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // 獲取所有學生清單
  const fetchAllStudents = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(config.getAllTeacherStudents);
      if (response.data.success) {
        // 確保 ID 是數字類型，並包含 class_name
        const formattedStudents = (response.data.data || []).map(student => ({
          ...student,
          id: parseInt(student.id),
          class_name: student.class_name || null
        }));
        setAllStudents(formattedStudents);
      } else {
        console.error('獲取學生清單失敗:', response.data.message);
        setAllStudents([]);
      }
    } catch (error) {
      console.error('獲取學生清單錯誤:', error);
      setAllStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // 獲取社群學生清單
  const fetchClassStudents = async () => {
    console.log('=== 開始獲取社群學生清單 ===');
    console.log('社群ID:', classData?.id);
    
    if (!classData?.id) {
      console.log('沒有社群ID，跳過獲取');
      return;
    }
    
    try {
      console.log('發送API請求:', `${config.getClassStudentsNew}${classData.id}`);
      const response = await axiosInstance.get(`${config.getClassStudentsNew}${classData.id}`);
      
      console.log('API回應:', response.data);
      
      if (response.data.success) {
        console.log('原始學生資料:', response.data.data);
        
        // 轉換資料格式以匹配前端需求
        const formattedStudents = response.data.data.map(student => {
          const studentId = student.Student?.id || student.student_id || student.id;
          return {
            id: parseInt(studentId), // 確保 ID 是數字類型
            name: student.Student?.name || student.student_name,
            email: student.Student?.email || student.student_email,
            studentNumber: student.Student?.studentNumber || student.student_number
          };
        });
        
        console.log('格式化後的學生資料:', formattedStudents);
        setClassStudents(formattedStudents);
        console.log('已更新社群學生狀態');
      } else {
        console.error('獲取社群學生失敗:', response.data.message);
        setClassStudents([]);
      }
    } catch (error) {
      console.error('獲取社群學生錯誤:', error);
      console.error('錯誤詳情:', error.response?.data);
      setClassStudents([]);
    }
  };

  useEffect(() => {
    if (open && classData) {
      fetchAllStudents();
      fetchClassStudents();
    }
  }, [open, classData]);

  // 獲取所有班級列表（用於篩選）
  const allClasses = React.useMemo(() => {
    const classes = new Set();
    allStudents.forEach(student => {
      if (student.class_name) {
        classes.add(student.class_name);
      }
    });
    return Array.from(classes).sort();
  }, [allStudents]);

  const filteredStudents = allStudents.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.studentNumber && student.studentNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesClass = !classFilter || student.class_name === classFilter;
    return matchesSearch && matchesClass;
  });

  const isStudentInClass = (studentId) => {
    const isInClass = classStudents.some(s => {
      // 確保 ID 比較時使用相同的類型
      const classStudentId = s.id;
      const targetId = studentId;
      console.log('比較ID:', { classStudentId, targetId, match: classStudentId == targetId });
      return classStudentId == targetId; // 使用 == 而不是 === 來處理類型轉換
    });
    console.log('學生是否在社群中:', { studentId, isInClass, classStudents: classStudents.map(s => ({ id: s.id, name: s.name })) });
    return isInClass;
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // 全選/取消全選
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedStudents([]);
      setSelectAll(false);
    } else {
      const availableStudents = filteredStudents.filter(student => !isStudentInClass(student.id));
      setSelectedStudents(availableStudents.map(s => s.id));
      setSelectAll(true);
    }
  };

  const handleBulkAdd = async () => {
    if (selectedStudents.length === 0) {
      onSuccess('請先選擇要加入的學生');
      return;
    }

    setBulkLoading(true);
    let successCount = 0;
    let errorCount = 0;
    const needsPasswordStudents = [];
    const usersForUserClass = [];

    console.log('開始批量加入學生:', selectedStudents);

    // 個別處理每個學生，確保每個都能正確處理
    for (const studentId of selectedStudents) {
      try {
        const student = allStudents.find(s => s.id === studentId);
        if (!student) {
          console.log('找不到學生:', studentId);
          errorCount++;
          continue;
        }

        console.log('處理學生:', student.name);

        // 直接加入社群（所有學生都應該是已註冊的）
        try {
          const response = await axiosInstance.post(
            `${config.addStudentToClass}${classData.id}/students`,
            { studentIds: [studentId] }
          );
          
          if (response.data.success) {
            console.log('學生加入成功:', student.name);
            successCount++;
            usersForUserClass.push({ userId: studentId, role: 'student' });
          } else {
            console.log('學生加入失敗:', student.name);
            errorCount++;
          }
        } catch (error) {
          console.log('加入學生時發生錯誤:', error.response?.data);
          console.log('其他錯誤:', error.message);
          errorCount++;
        }
      } catch (error) {
        console.error('處理學生時發生錯誤:', error);
        errorCount++;
      }
    }

    console.log('批量操作完成');

    // 同步建立 UserClass（批量）
    try {
      if (usersForUserClass.length > 0) {
        const payload = { classId: Number(classData.id), users: usersForUserClass.map(u => ({ userId: Number(u.userId), role: u.role })) };
        console.log('[UserClass] batch-join request (bulk):', payload);
        const ucRes = await axiosInstance.post('/api/userClasses/batch-join', payload);
        console.log('[UserClass] batch-join response (bulk):', ucRes.status, ucRes.data);
      }
    } catch (e) {
      console.warn('同步建立 UserClass 失敗（批量）:', e.response?.data || e.message);
    }

    // 更新社群學生清單
    await fetchClassStudents();
    setSelectedStudents([]);
    setSelectAll(false); // 重置全選狀態
    
    let message = `批量操作完成：成功 ${successCount} 位`;
    if (errorCount > 0) message += `，失敗 ${errorCount} 位`;
    
    console.log('批量操作結果:', message);
    onSuccess(message);
    setBulkLoading(false);
  };

  const handleAddStudentToClass = async (student) => {
    console.log('=== 開始加入學生到社群 ===');
    console.log('學生資料:', student);
    console.log('社群ID:', classData.id);
    console.log('學生是否已在社群中:', isStudentInClass(student.id));
    console.log('當前社群學生列表:', classStudents);
    
    if (!isStudentInClass(student.id)) {
      try {
        console.log('發送API請求:', `${config.addStudentToClass}${classData.id}/students`);
        console.log('請求資料:', { studentIds: [student.id] });
        
        // 直接將學生加入社群（所有學生都應該是已註冊的）
        const response = await axiosInstance.post(
          `${config.addStudentToClass}${classData.id}/students`,
          { studentIds: [student.id] }
        );
        
        console.log('API回應:', response.data);
        
        if (response.data.success) {
          // 同步建立 UserClass（單筆）
          try {
            const payload = {
              classId: Number(classData.id),
              users: [{ userId: Number(student.id), role: 'student' }]
            };
            console.log('[UserClass] batch-join request:', payload);
            const ucRes = await axiosInstance.post('/api/userClasses/batch-join', payload);
            console.log('[UserClass] batch-join response:', ucRes.status, ucRes.data);
          } catch (e) {
            console.warn('同步建立 UserClass 失敗（單筆）:', e.response?.data || e.message);
          }
          console.log('加入成功，重新獲取社群學生列表');
          await fetchClassStudents();
          onSuccess(`已將 ${student.name} 加入社群`);
        } else {
          console.log('加入失敗:', response.data.message);
          onSuccess(`加入失敗: ${response.data.message}`);
        }
      } catch (error) {
        console.error('加入學生到社群錯誤:', error);
        console.error('錯誤詳情:', error.response?.data);
        onSuccess(`加入失敗: ${error.response?.data?.message || '網路錯誤'}`);
      }
    } else {
      console.log('學生已在社群中，跳過加入操作');
    }
  };

  const handleRemoveStudentFromClass = async (studentId) => {
    console.log('=== 開始從社群移除學生 ===');
    console.log('學生ID:', studentId);
    console.log('社群ID:', classData.id);
    
    const student = classStudents.find(s => s.id === studentId);
    console.log('找到的學生:', student);
    
    try {
      console.log('發送刪除API請求:', `${config.addStudentToClass}${classData.id}/students/${studentId}`);
      
      const response = await axiosInstance.delete(
        `${config.addStudentToClass}${classData.id}/students/${studentId}`
      );
      
      console.log('刪除API回應:', response.data);
      
      if (response.data.success) {
        console.log('移除成功，重新獲取社群學生列表');
        // 重新獲取社群學生清單
        await fetchClassStudents();
        onSuccess(`已將 ${student?.name} 從社群移除`);
      } else {
        console.log('移除失敗:', response.data.message);
        onSuccess(`移除失敗: ${response.data.message}`);
      }
    } catch (error) {
      console.error('從社群移除學生錯誤:', error);
      console.error('錯誤詳情:', error.response?.data);
      onSuccess(`移除失敗: ${error.response?.data?.message || '網路錯誤'}`);
    }
  };

  if (!classData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      sx={{
        zIndex: 2000,
        '& .MuiDialog-paper': {
          zIndex: 2000,
          maxHeight: '90vh'
        }
      }}
      PaperProps={{
        sx: {
          zIndex: 2000,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ color: '#2b687f' }} />
          <Typography variant="h6">{classData.name} - 學生管理</Typography>
          </Box>
        </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            管理此社群的學生，可以從所有學生清單中選擇學生加入社群。
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {/* 左欄：未加入學生 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              {/* 標題和操作按鈕區域 */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#666' }}>
                    未加入學生
                  </Typography>
                  <Chip 
                    label={filteredStudents.filter(s => !isStudentInClass(s.id)).length} 
                    size="small" 
                    color="default"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSelectAll}
                    sx={{ 
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1.5,
                      height: '32px'
                    }}
                  >
                    {selectAll ? '取消全選' : '全選'}
                  </Button>
                  {selectedStudents.length > 0 && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleBulkAdd}
                      disabled={bulkLoading}
                      sx={{ 
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        px: 1.5,
                        height: '32px'
                      }}
                    >
                      {bulkLoading ? '加入中...' : `批量加入 (${selectedStudents.length})`}
                    </Button>
                  )}
                </Box>
              </Box>
              
              {/* 搜尋和篩選區域 */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="搜尋學生..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                />
                <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                  <InputLabel>班級篩選</InputLabel>
                  <Select
                    value={classFilter}
                    onChange={(e) => setClassFilter(e.target.value)}
                    label="班級篩選"
                  >
                    <MenuItem value="">全部班級</MenuItem>
                    {allClasses.map(className => (
                      <MenuItem key={className} value={className}>{className}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {classFilter && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // 根據篩選結果自動選取所有符合條件的學生
                      const availableStudents = filteredStudents.filter(s => !isStudentInClass(s.id));
                      setSelectedStudents(availableStudents.map(s => s.id));
                      setSelectAll(true);
                    }}
                    sx={{ 
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: 1.5,
                      height: '40px' // 與 TextField 和 Select 的高度一致
                    }}
                  >
                    選取此班級
                  </Button>
                )}
              </Box>
            </Box>
            <Box sx={{ 
              border: '2px solid #e0e0e0', 
              borderRadius: 2, 
              maxHeight: '50vh', 
              overflow: 'auto',
              p: 1,
              backgroundColor: '#fafafa'
            }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                filteredStudents.filter(s => !isStudentInClass(s.id)).map((student) => (
                <Box key={student.id} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 0.5,
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: selectedStudents.includes(student.id) ? '#e3f2fd' : 'transparent',
                  '&:hover': { backgroundColor: selectedStudents.includes(student.id) ? '#e3f2fd' : '#f5f5f5' },
                  minHeight: '40px'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Checkbox
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleStudentSelect(student.id)}
                      size="small" 
                      sx={{ mr: 0.5 }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
                        {student.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {student.email}
                        </Typography>
                        {student.class_name && (
                          <Chip
                            label={student.class_name}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.65rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleAddStudentToClass(student)}
                    sx={{ 
                      fontSize: '0.75rem',
                      minWidth: '60px',
                      height: '28px',
                      px: 1.5
                    }}
                  >
                    加入
                  </Button>
                        </Box>
                ))
              )}
          </Box>
          </Grid>

          {/* 右欄：已加入學生 */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#2b687f' }}>
                已加入學生
              </Typography>
              <Chip 
                label={classStudents.length} 
                size="small" 
                color="primary"
              />
            </Box>
            <Box sx={{ 
              border: '2px solid #2b687f', 
              borderRadius: 2, 
              maxHeight: '50vh', 
              overflow: 'auto',
              p: 1,
              backgroundColor: '#f0f8ff'
            }}>
              {classStudents.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    暫無學生
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    從左側選擇學生加入社群
                  </Typography>
                </Box>
              ) : (
                classStudents.map((student) => (
                  <Box key={student.id} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e3f2fd',
                    '&:hover': { 
                      backgroundColor: '#e3f2fd',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.2s',
                    minHeight: '48px'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem', bgcolor: '#2b687f', mr: 1 }}>
                        {student.name ? student.name.charAt(0) : '?'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="600" sx={{ fontSize: '0.9rem' }}>
                          {student.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {student.email}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveStudentFromClass(student.id)}
                      sx={{ 
                        color: '#f44336',
                        '&:hover': { backgroundColor: '#ffebee' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
        </DialogContent>
      
        <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
         <Button onClick={onClose}>關閉</Button>
        </DialogActions>
    </Dialog>
  );
}

// 分組設定對話框組件
function GroupSettingDialog({ open, classData, onClose, onSuccess }) {
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupCount, setGroupCount] = useState(3);
  const [draggedStudent, setDraggedStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [unassignedStudents, setUnassignedStudents] = useState([]);

  // 計算未分配的學生
  const calculateUnassignedStudents = () => {
    const assignedStudentIds = groups.flatMap(g => 
      (g.GroupMembers || []).map(gm => gm.user_id)
    );
    const unassigned = students.filter(s => !assignedStudentIds.includes(s.id));
    setUnassignedStudents(unassigned);
  };

  // 獲取社群學生清單
  const fetchClassStudents = async () => {
    if (!classData?.id) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${config.getClassStudentsNew}${classData.id}`);
      if (response.data.success) {
        const formattedStudents = response.data.data.map(student => ({
          id: student.student_id || student.id,
          name: student.Student?.name || student.student_name,
          email: student.Student?.email || student.student_email
        }));
        setStudents(formattedStudents);
      }
    } catch (error) {
      console.error('獲取社群學生錯誤:', error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // 獲取現有分組
  const fetchGroups = async () => {
    if (!classData?.id) return;
    
    try {
      const response = await axiosInstance.get(`${config.getClassGroups}${classData.id}/groups`);
      if (response.data.success) {
        setGroups(response.data.data || []);
      }
    } catch (error) {
      console.error('獲取分組錯誤:', error);
      setGroups([]);
    }
  };

  useEffect(() => {
    if (open && classData) {
      fetchClassStudents();
      fetchGroups();
    }
  }, [open, classData]);

  // 當分組或學生變更時，重新計算未分配學生
  useEffect(() => {
    if (students.length > 0 || groups.length > 0) {
      calculateUnassignedStudents();
    }
  }, [students, groups]);

  // 新增一個空白分組
  const handleAddEmptyGroup = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.createGroup, {
        classId: classData.id,
        groupName: `第${groups.length + 1}組`,
        type: 'default'
      });
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組
        setSnackbar({ 
          open: true, 
          message: '新組別已新增', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('新增分組錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '新增分組失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 拖曳開始
  const handleDragStart = (student) => {
    setDraggedStudent(student);
  };

  // 點擊學生選擇
  const handleStudentClick = (student) => {
    if (selectedStudent?.id === student.id) {
      setSelectedStudent(null); // 取消選擇
    } else {
      setSelectedStudent(student); // 選擇學生
    }
  };

  // 拖曳或點擊分配到分組（或取消分配）
  const handleAssignToGroup = async (groupId) => {
    const student = draggedStudent || selectedStudent;
    if (!student) return;
    
    // 檢查學生是否已經在該組中
    const group = groups.find(g => g.id === groupId);
    const isAlreadyInGroup = group?.GroupMembers?.some(m => m.user_id === student.id);
    
    if (isAlreadyInGroup) {
      // 如果已經在該組中，則取消分配
      await handleRemoveFromGroup(groupId, student.id);
      return;
    }
    
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.assignStudentToGroup, {
        groupId: groupId,
        userId: student.id
      });
      
      if (response.data.success) {
        await fetchGroups();
        setSnackbar({ 
          open: true, 
          message: `已將 ${student.name} 分配到該組`, 
          severity: 'success' 
        });
        setDraggedStudent(null);
        setSelectedStudent(null);
      }
    } catch (error) {
      console.error('分配學生錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || '分配學生失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 從分組移除學生
  const handleRemoveFromGroup = async (groupId, studentId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(config.removeStudentFromGroup, {
        groupId: groupId,
        userId: studentId
      });
      
      if (response.data.success) {
        await fetchGroups();
        setSnackbar({ 
          open: true, 
          message: '學生已從分組移除', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('移除學生錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '移除學生失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 編輯分組名稱
  const handleEditGroup = (group) => {
    setEditingGroup({ ...group });
  };

  // 儲存分組名稱
  const handleSaveGroup = async (groupId) => {
    if (!editingGroup || editingGroup.id !== groupId) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.put(`${config.updateGroup}${groupId}`, {
        groupName: editingGroup.groupName
      });
      
      if (response.data.success) {
        await fetchGroups(); // 重新獲取分組資料
        setEditingGroup(null);
        setSnackbar({ 
          open: true, 
          message: '分組名稱已更新', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('更新分組錯誤:', error);
      setSnackbar({ 
        open: true, 
        message: '更新分組失敗', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // 刪除分組
  const handleDeleteGroup = async (groupId) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`${config.deleteGroup}${groupId}`);
      if (response.data.success) {
        await fetchGroups();
        setSnackbar({
          open: true,
          message: '分組已刪除',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `刪除失敗: ${response.data.message}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('刪除分組錯誤:', error);
      // 直接顯示後端的錯誤訊息，後端會返回詳細的說明
      const backendMsg = error?.response?.data?.message || '';
      setSnackbar({
        open: true,
        message: backendMsg || '刪除分組失敗',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!classData) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      sx={{
        zIndex: 2000,
        '& .MuiDialog-paper': {
          zIndex: 2000,
          maxHeight: '90vh'
        }
      }}
      PaperProps={{
        sx: {
          zIndex: 2000,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GroupIcon sx={{ color: '#2b687f' }} />
          <Typography variant="h6">{classData.name} - 預設分組設定</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            設定此社群的預設分組，拖曳學生到分組中或點擊學生來分配。
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              className="btn-primary"
              onClick={handleAddEmptyGroup}
              startIcon={<AddIcon />}
              disabled={loading}
            >
              新增小組
            </Button>
            <Typography variant="body2" color="text.secondary">
              目前共 {groups.length} 個組別
            </Typography>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          {/* 左欄：未分配學生 */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              未分配學生 ({unassignedStudents.length})
            </Typography>
            {selectedStudent && (
              <Typography variant="caption" color="primary" sx={{ mb: 1, display: 'block' }}>
                已選擇：{selectedStudent.name} - 點擊右側分組來分配
              </Typography>
            )}
            <Box sx={{ 
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 2,
              minHeight: '300px',
              backgroundColor: '#fafafa'
            }}>
              {unassignedStudents.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {unassignedStudents.map(student => (
                    <Chip
                      key={student.id}
                      label={student.name}
                      draggable
                      onDragStart={() => handleDragStart(student)}
                      onClick={() => handleStudentClick(student)}
                      color={selectedStudent?.id === student.id ? 'primary' : 'default'}
                      variant={selectedStudent?.id === student.id ? 'filled' : 'outlined'}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#e3f2fd' }
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                  所有學生都已分配
                </Typography>
              )}
            </Box>
          </Grid>

          {/* 右欄：分組列表 */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              分組列表 ({groups.length} 組)
            </Typography>
            
            {groups.length > 0 ? (
              <Grid container spacing={2} sx={{ maxHeight: '500px', overflow: 'auto' }}>
                {groups.map((group, index) => {
                  const isStudentInGroup = (draggedStudent || selectedStudent) && 
                    group.GroupMembers?.some(m => m.user_id === (draggedStudent || selectedStudent)?.id);
                  return (
                  <Grid item xs={12} sm={6} key={group.id}>
                    <Box 
                      sx={{ 
                        border: (draggedStudent || selectedStudent) 
                          ? (isStudentInGroup ? '2px solid #f44336' : '2px solid #1976d2')
                          : '2px solid #e0e0e0', 
                        borderRadius: 2, 
                        p: 2,
                        backgroundColor: '#ffffff',
                        transition: 'all 0.2s',
                        cursor: (draggedStudent || selectedStudent) ? 'pointer' : 'default',
                        '&:hover': {
                          borderColor: (draggedStudent || selectedStudent) 
                            ? (isStudentInGroup ? '#f44336' : '#1976d2')
                            : '#2b687f',
                          boxShadow: (draggedStudent || selectedStudent) ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAssignToGroup(group.id);
                      }}
                      onClick={() => (draggedStudent || selectedStudent) && handleAssignToGroup(group.id)}
                    >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      {editingGroup?.id === group.id ? (
                        <TextField
                          value={editingGroup.groupName}
                          onChange={(e) => setEditingGroup({ ...editingGroup, groupName: e.target.value })}
                          size="small"
                          variant="outlined"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          sx={{ flex: 1, mr: 1 }}
                        />
                      ) : (
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2b687f' }}>
                          {group.groupName} <span style={{ color: '#666', fontSize: '0.9rem' }}>({group.GroupMembers?.length || 0} 人)</span>
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {editingGroup?.id === group.id ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveGroup(group.id);
                              }}
                              sx={{ color: '#2b687f' }}
                              title="確認"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingGroup(null);
                              }}
                              title="取消"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingGroup({ ...group });
                              }}
                              sx={{ color: '#2b687f' }}
                              title="編輯名稱"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGroup(group.id);
                              }}
                              sx={{ color: '#d32f2f' }}
                              title="刪除分組"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      minHeight: '60px',
                      border: '2px dashed #ccc',
                      borderRadius: 1,
                      p: 1.5,
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1
                    }}>
                      {group.GroupMembers && group.GroupMembers.length > 0 ? (
                        group.GroupMembers.map(member => (
                          <Chip 
                            key={member.id} 
                            label={member.User?.name || '未知'} 
                            size="small"
                            onDelete={() => handleRemoveFromGroup(group.id, member.user_id)}
                            sx={{ 
                              backgroundColor: '#e3f2fd',
                              '& .MuiChip-deleteIcon': {
                                color: '#1976d2',
                                '&:hover': { color: '#d32f2f' }
                              }
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', width: '100%', textAlign: 'center', py: 2 }}>
                          {(draggedStudent || selectedStudent) 
                            ? (isStudentInGroup 
                                ? `點擊取消 ${(draggedStudent || selectedStudent).name} 的分配`
                                : `點擊或拖曳將 ${(draggedStudent || selectedStudent).name} 分配到此組`)
                            : '拖曳學生到此處或點擊學生來分配'}
                        </Typography>
                      )}
                      {isStudentInGroup && group.GroupMembers && group.GroupMembers.length > 0 && (
                        <Typography variant="caption" color="error" sx={{ width: '100%', display: 'block', mt: 0.5 }}>
                          再次點擊可取消分配 {(draggedStudent || selectedStudent).name}
                        </Typography>
                      )}
                    </Box>
                    </Box>
                  </Grid>
                );
                })}
              </Grid>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6,
                border: '2px dashed #ccc',
                borderRadius: 2,
                backgroundColor: '#fafafa'
              }}>
                <GroupIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  尚未建立分組
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  點擊「新增小組」開始建立分組
                </Typography>
              </Box>
            )}
              </Grid>
            </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
         <Button onClick={onClose} disabled={loading}>關閉</Button>
      </DialogActions>

      {/* Snackbar 通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

export default ClassManagement;